import {
  Button,
  Chip,
  Grid,
  Link as LinkMui,
  TableCell,
  TableRow,
  TableHead,
  Table,
  Typography,
  TableBody,
  Container,
  Skeleton,
} from '@mui/material'
import { Link as LinkRouter, useParams, useNavigate } from 'react-router-dom'
import { ChevronLeft } from '@mui/icons-material'
import { receivePaymentType, invoiceType } from '../../types'
import { useEffect, useState } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import './Payment.css'
import ErrorScreen from '../../Misc/ErrorScreen'

function Payment() {
  type paymentDetail = receivePaymentType & {
    customer_name: string
    parent_customer_name: string
    invoice_number: string
  }
  const { id } = useParams()
  const navigate = useNavigate()
  const [payment, setPayment] = useState<paymentDetail | null>(null)
  const [invoices, setInvoices] = useState<invoiceType[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (id) {
      setLoading(true)
      setLoadError(false)
      axios
        .get(`/payment/${id}`)
        .then(res => {
          setPayment(res.data.receive_payment)
          setInvoices(res.data.related_invoices)
          setLoadError(false)
        })
        .catch(err => {
          console.error(err)
          setLoadError(true)
        })
        .finally(() => setLoading(false))
    }
  }, [])

  useEffect(() => {
    document.title = `Payment ${payment?.ref_number ? `| ${payment.ref_number}` : ''} | Salestrack`
  }, [payment])

  return (
    <div className="payment-detail">
      <div className="payment-detail-button-container">
        <Button
          size="large"
          startIcon={<ChevronLeft />}
          variant="contained"
          className="payment-detail-button"
          component={LinkRouter}
          to="/customer/payments"
          loading={loading}
          onClick={e => {
            e.preventDefault()
            navigate(-1)
          }}
        >
          Return
        </Button>
        {loading ? (
          <Skeleton width={280} height={32} />
        ) : (
          <Chip label={<Typography variant="h6">Payment Reference #: {payment?.ref_number}</Typography>} />
        )}
      </div>
      {loadError ? (
        <div className="payment-detail-error-container">
          <ErrorScreen
            errorText="Failed to load payment details. Please try again or contact support if the error persists."
            title="Payment Load Failed"
          />
        </div>
      ) : (
        <div className="payment-detail-data-container">
          <div className="payment-detail-data-container-header">
            <Container className="payment-detail-header-container" maxWidth={false}>
              <Container className="payment-detail-header-column" maxWidth={false}>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={100} /> : 'Customer Name:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={120} /> : payment?.parent_name ? payment.parent_name : '-'}
                  </Typography>
                </Grid>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={80} /> : 'Job Name:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={100} /> : payment?.customer_name ? payment.customer_name : '-'}
                  </Typography>
                </Grid>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={110} /> : 'Invoice Number:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={120} /> : payment?.invoice_ref_number}
                  </Typography>
                </Grid>
              </Container>
              <Container className="payment-detail-header-column" maxWidth={false}>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={120} /> : 'Payment Method:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={100} /> : payment?.payment_method ? payment.payment_method : '-'}
                  </Typography>
                </Grid>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={60} /> : 'Memo:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? <Skeleton width={150} /> : payment?.memo ? payment.memo : '-'}
                  </Typography>
                </Grid>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={120} /> : 'Transaction Date:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={100} />
                    ) : payment?.txn_date ? (
                      dayjs(payment.txn_date).format('MM/DD/YYYY')
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
              </Container>
              <Container className="payment-detail-header-column" maxWidth={false}>
                <Grid className="payment-detail-header-grid">
                  <Typography variant="body2" color="text.secondary">
                    {loading ? <Skeleton width={100} /> : 'Total Amount:'}
                  </Typography>
                  <Typography variant="body1">
                    {loading ? (
                      <Skeleton width={100} />
                    ) : payment?.total_amount ? (
                      payment.total_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                    ) : (
                      '-'
                    )}
                  </Typography>
                </Grid>
              </Container>
            </Container>
          </div>
          <div className="payment-detail-data-container-related-items">
            {loading ? <Skeleton width={180} height={32} /> : <Typography variant="h6">Related Items</Typography>}
            <Table color="primary">
              <TableHead>
                {loading ? (
                  <TableRow>
                    {Array.from({ length: 6 }).map((_, idx) => (
                      <TableCell key={idx}>
                        <Skeleton className="skeleton-header" />
                      </TableCell>
                    ))}
                  </TableRow>
                ) : (
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Reference Number</TableCell>
                    <TableCell align="right">Transaction Date</TableCell>
                    <TableCell align="right">Due Date</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                )}
              </TableHead>
              <TableBody>
                {loading
                  ? Array.from({ length: 3 }).map((_, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Skeleton width={120} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell align="right">
                          <Skeleton width={100} />
                        </TableCell>
                        <TableCell>
                          <Skeleton width={80} />
                        </TableCell>
                      </TableRow>
                    ))
                  : invoices.map(i => (
                      <TableRow key={i.invoice_txn_id}>
                        <TableCell>Invoice</TableCell>
                        <TableCell align="right">
                          <LinkMui to={`/customer/invoice/${i.invoice_txn_id}`} component={LinkRouter}>
                            {i.ref_number}
                          </LinkMui>
                        </TableCell>
                        <TableCell align="right">{i.txn_date ? dayjs(i.txn_date).format('MM/DD/YYYY') : '-'}</TableCell>
                        <TableCell align="right">{i.due_date ? dayjs(i.due_date).format('MM/DD/YYYY') : '-'}</TableCell>
                        <TableCell align="right">
                          {i.applied_amount
                            ? i.applied_amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
                            : '-'}
                        </TableCell>
                        <TableCell>{i.is_paid ? 'Paid' : 'Not Paid'}</TableCell>
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </div>
  )
}

export default Payment
